import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { GridColumn } from '@progress/kendo-react-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import {
  ContextMenu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import { plusIcon } from '@progress/kendo-svg-icons';

import { useKeyPress } from '../../../../hooks';

import { PTOCodesService } from '../../../../services/setup/PTOCodesService';
import { PTO_UNITS, PTO_ACCRUAL_START, PTO_PERIODS } from '../../../../constants';
import { IPTOCode } from '../../../../data/types';
import { columns } from './model';
import { Button, ListingGrid } from '../../../common';
import { ButtonWrapper, ListingGridWrapper } from '../PayrollSetup.styled';
import PTOCodesDialog from './TimeOffCodesDialog';

type SelectionType = { field?: string; dataItem: any };

interface PTOCodesProps {
  selectedPTOCode: IPTOCode | undefined;
  setSelectedPTOCode: React.Dispatch<
    React.SetStateAction<IPTOCode | undefined>
  >;
};

const TimeOffCodes: FC<PTOCodesProps> = ({
  selectedPTOCode,
  setSelectedPTOCode,
}) => {
  const [originData, setOriginData] = useState<IPTOCode[]>([]);
  const [filterData, setFilterData] = useState<IPTOCode[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<SortDescriptor[]>();
  const [showContext, setShowContext] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SelectionType>();

  // const [showNewForm, setShowNewForm] = useState(false);
  // const [showOldForm, setShowOldForm] = useState(false);

  const offset = useRef({ left: 0, top: 0 });

  async function fetchData() {
    try {
      const PTOCodeList: IPTOCode[] = await PTOCodesService.getPTOCodes();
      const formattedData = PTOCodeList.map((item) => {
        return {
          ...item,
          PTOUnits: PTO_UNITS.find((option) => option.key === item.PTOUnits)?.name,
          AccrualStarts: PTO_ACCRUAL_START.find((option) => option.key === item.AccrualStarts)?.name,
          TenurePeriods: PTO_PERIODS.find((option) => option.key === item.TenurePeriods)?.name,
          RollOverPeriods: PTO_PERIODS.find((option) => option.key === item.RollOverPeriods)?.name,
        };
      });
      setOriginData(formattedData ?? []);
      setFilterData(formattedData ?? []);
    } catch (e) { }

    setIsLoading(false);
  }

  useEffect(() => {
    setIsLoading(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContextMenuOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    offset.current = {
      left: e.pageX,
      top: e.pageY,
    };
    setShowContext(true);
  };

  const handleOnSelect = useCallback(
    (e: MenuSelectEvent) => {
      if (selectedItem) {
        const { field, dataItem } = selectedItem;
        switch (e.item.data.action) {
          case 'sortAsc':
            setSort([
              {
                dir: 'asc',
                field: field ?? '',
              },
            ]);
            break;
          case 'sortDesc':
            setSort([
              {
                dir: 'desc',
                field: field ?? '',
              },
            ]);
            break;
          case 'filterIn':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] === dataItem[field]
                )
              );
            }
            break;
          case 'filterOut':
            if (field) {
              setFilterData(
                originData.filter(
                  (data: any) => data[field] !== dataItem[field]
                )
              );
            }
            break;
          case 'removeFilters':
            setFilterData(originData);
            break;
          default:
        }
      }
      setShowContext(false);
    },
    [selectedItem, originData]
  );

  useKeyPress(
    () => {
    },
    () => {
    },
    () => {
    },
    () => {
      setSelectedPTOCode({ PayCode: '' });
    }
  );

  return (
    <ListingGridWrapper>
      {isLoading ? (
        <></>
      ) : (
        <>
          <ListingGrid
            rowHeight={24}
            data={sort ? orderBy(filterData, sort) : filterData}
            dataItemKey="PTOCode"
            onRowClick={(e) => {
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.PayCode === e.dataItem.PTOCode,
                }))
              );
            }}
            onRowDoubleClick={(e) => {
              // setShowNewForm(true)
              // setShowOldForm(false)
              setSelectedPTOCode(e.dataItem);
            }}
            sortable={true}
            sort={sort}
            onSortChange={(e) => {
              setSort(e.sort);
            }}
            onContextMenu={(event) => {
              handleContextMenuOpen(event.syntheticEvent);
              const { dataItem, field } = event;
              setFilterData((prev) =>
                prev.map((item) => ({
                  ...item,
                  selected: item.PayCode === dataItem.PTOCode,
                }))
              );
              setSelectedItem({ field, dataItem });
            }}
          >
            {columns.map((column, index) => {
              return <GridColumn {...column} key={index} />;
            })}
          </ListingGrid>
          <ButtonWrapper>
            <Button
              onClick={(e) => {
                e.preventDefault();
                // setShowOldForm(true)
                // setShowNewForm(false)
                setSelectedPTOCode({ PayCode: '' });
              }}
              id="create-btn"
              title="Add New"
              svgIcon={plusIcon}
            >
              ADD <u>N</u>EW
            </Button>
          </ButtonWrapper>
        </>
      )}

      <ContextMenu
        show={showContext}
        offset={offset.current}
        onSelect={handleOnSelect}
        onClose={() => setShowContext(false)}
        className="context-menu"
      >
        <MenuItem
          text="Sort Ascending"
          data={{
            action: 'sortAsc',
          }}
        />
        <MenuItem
          text="Sort Descending"
          data={{
            action: 'sortDesc',
          }}
        />
        <MenuItem
          text="Filter In Selected"
          data={{
            action: 'filterIn',
          }}
        />
        <MenuItem
          text="Filter Out Selected"
          data={{
            action: 'filterOut',
          }}
        />
        <MenuItem
          text="Remove All Filters"
          data={{
            action: 'removeFilters',
          }}
        />
      </ContextMenu>
      {selectedPTOCode && (
        <PTOCodesDialog
          handleClose={(shouldRefreshData) => {
            setSelectedPTOCode(undefined);
            if (shouldRefreshData) {
              fetchData();
            }
          }}
          data={selectedPTOCode}
          isEdit={selectedPTOCode?.PayCode ? true : false}
        />
      )}

      {/* {selectedPTOCode && showOldForm && !showNewForm && (
        <OldForm
          handleClose={(shouldRefreshData) => {
            setSelectedPTOCode(undefined);
            if (shouldRefreshData) {
              fetchData();
            }
          }}
          data={selectedPTOCode}
          isEdit={selectedPTOCode?.PayCode ? true : false}
        />
      )} */}
    </ListingGridWrapper>
  );
};

export default TimeOffCodes;
